import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Undo2, Printer, Download, Send, CloudOff, Check } from "lucide-react";
import plannerApi from "@/lib/plannerApi";
import { fmtINR } from "@/lib/portalApi";
import { PlannerShell } from "@/components/planner/PlannerShell";
import { BrowsePanel } from "@/components/planner/BrowsePanel";
import { ListItemRow } from "@/components/planner/ListItemRow";
import { QuoteRequestModal } from "@/components/planner/QuoteRequestModal";
import { DepartmentsEditor } from "@/components/planner/DepartmentsEditor";
import { usePlannerList } from "@/components/planner/usePlannerList";

const newLine = (extra) => ({
    id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
    item_id: "", generic: "", category: "", brand_preference: "", alt_brands: "", pack_size: "", uom: "",
    qty: 1, frequency: "monthly", substitution_allowed: true, department: "", delivery_location: "",
    notes: "", skip_this_month: false, rate: null, tax_percent: null, ...extra,
});

export default function PlannerBuilder() {
    const { id } = useParams();
    const { list, items, meta, status, canUndo, setItems, setMeta, undo, retry } = usePlannerList(id);
    const [profile, setProfile] = useState(null);
    const [tab, setTab] = useState("browse");
    const [quoteOpen, setQuoteOpen] = useState(false);
    const isBiz = list?.planner_type === "BUSINESS";

    useEffect(() => {
        if (list?.planner_type) plannerApi.get(`/profile/${list.planner_type}`).then((r) => setProfile(r.data || {}));
    }, [list?.planner_type]);

    useEffect(() => {
        if (isBiz && profile?.departments?.length && meta.departments.length === 0)
            setMeta({ departments: profile.departments.map((d) => ({ name: d, delivery_location: "" })) });
        // eslint-disable-next-line
    }, [isBiz, profile]);

    const ids = useMemo(() => new Set(items.map((i) => i.item_id).filter(Boolean)), [items]);
    const customs = useMemo(() => new Set(items.filter((i) => !i.item_id).map((i) => i.name.toLowerCase())), [items]);
    const inList = (itemId, generic) => (itemId ? ids.has(itemId) : customs.has((generic || "").toLowerCase()));
    const estimate = items.reduce((s, i) => (i.rate && !i.skip_this_month ? s + i.qty * i.rate * (1 + (i.tax_percent || 0) / 100) : s), 0);
    const unresolved = items.filter((i) => !i.item_id && !i.skip_this_month).length;

    const addProduct = (m, section, generic) => setItems((prev) => [...prev, newLine({
        item_id: m.item_id, name: m.name, generic, category: section, rate: m.rate, tax_percent: m.tax_percent, uom: "",
    })]);
    const addCustom = (name, section) => name && setItems((prev) => [...prev, newLine({ name, generic: name, category: section })]);

    if (!list) return <PlannerShell wide><div className="p-16 text-center text-slate-400"><Loader2 className="inline animate-spin" size={18}/></div></PlannerShell>;

    const exportCsv = () => {
        const head = ["Item", "Qty", "Unit", "Pack size", "Brand", "Alt brands", "Frequency", "Department", "Substitutes OK", "Notes", "Status"];
        const rows = items.map((i) => [i.name, i.qty, i.uom, i.pack_size, i.brand_preference, i.alt_brands, i.frequency, i.department,
                                       i.substitution_allowed ? "Yes" : "No", i.notes, i.item_id ? "Matched" : "To confirm"]);
        const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `${meta.name || "essentials-list"}.csv`;
        a.click();
    };

    return (
        <PlannerShell wide>
            <div data-testid="planner-builder">
                <div className="flex flex-wrap items-center gap-3 mb-5 print:hidden">
                    <input value={meta.name} onChange={(e) => setMeta({ name: e.target.value })} data-testid="planner-list-name"
                           className="text-2xl sm:text-3xl font-black text-slate-900 bg-transparent outline-none border-b-2 border-transparent focus:border-emerald-500 min-w-0 flex-1"
                           style={{ fontFamily: "'Chivo',sans-serif" }}/>
                    <SaveBadge status={status} retry={retry}/>
                </div>

                <div className="lg:hidden flex rounded-xl bg-white border border-slate-200 p-1 mb-4 print:hidden">
                    {[["browse", "Add items"], ["list", `My list (${items.length})`]].map(([k, l]) => (
                        <button key={k} onClick={() => setTab(k)} data-testid={`planner-tab-${k}`}
                                className={`flex-1 py-2 rounded-lg text-sm font-bold ${tab === k ? "bg-slate-900 text-white" : "text-slate-600"}`}>{l}</button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6 print:block">
                    <div className={`${tab === "browse" ? "" : "hidden"} lg:block print:hidden min-w-0`}>
                        <BrowsePanel plannerType={list.planner_type} establishment={profile?.establishment_type || ""}
                                     inList={inList} onAddProduct={addProduct} onAddCustom={addCustom}/>
                    </div>
                    <aside className={`${tab === "list" ? "" : "hidden"} lg:block min-w-0`}>
                        <div className="lg:sticky lg:top-20 rounded-2xl bg-white border border-slate-200 p-4" data-testid="planner-my-list">
                            <div className="flex items-center justify-between print:hidden">
                                <div className="font-black text-slate-900">My list · <span data-testid="planner-item-count">{items.length}</span> items</div>
                                <div className="flex gap-1">
                                    <IconBtn onClick={undo} disabled={!canUndo} title="Undo" tid="planner-undo"><Undo2 size={14}/></IconBtn>
                                    <IconBtn onClick={() => window.print()} title="Print" tid="planner-print"><Printer size={14}/></IconBtn>
                                    <IconBtn onClick={exportCsv} title="Download CSV" tid="planner-export"><Download size={14}/></IconBtn>
                                </div>
                            </div>
                            {isBiz && <DepartmentsEditor departments={meta.departments} onChange={(d) => setMeta({ departments: d })}/>}
                            <div className="space-y-2 mt-3 lg:max-h-[52vh] lg:overflow-y-auto pr-1 print:max-h-none">
                                {items.length === 0 && <p className="text-sm text-slate-400 py-8 text-center" data-testid="planner-list-empty">Tick items from the checklist or search to start your list.</p>}
                                {items.map((it, i) => (
                                    <ListItemRow key={it.id} it={it} idx={i} isBiz={isBiz} departments={meta.departments}
                                                 onChange={(v) => setItems((prev) => prev.map((x) => (x.id === it.id ? v : x)))}
                                                 onRemove={() => setItems((prev) => prev.filter((x) => x.id !== it.id))}/>
                                ))}
                            </div>
                            <div className="border-t border-slate-100 mt-3 pt-3 print:hidden">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Estimate at current rates</span>
                                    <b className="font-mono" data-testid="planner-estimate">{fmtINR(estimate)}</b>
                                </div>
                                {unresolved > 0 && <p className="text-[11px] text-amber-700 mt-1">{unresolved} item(s) will be confirmed and priced in your quotation.</p>}
                                <button onClick={() => setQuoteOpen(true)} disabled={items.length === 0} data-testid="planner-request-quote"
                                        className="mt-3 w-full px-4 py-3 rounded-xl bg-emerald-600 text-white font-black hover:bg-emerald-500 disabled:opacity-40 inline-flex items-center justify-center gap-2">
                                    <Send size={15}/> Request quotation
                                </button>
                                <p className="text-[11px] text-slate-400 mt-2 text-center">Savings apply to the overall basket and aren't guaranteed on every item.</p>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
            {quoteOpen && <QuoteRequestModal list={list} estimate={estimate} unresolved={unresolved}
                                             onClose={() => setQuoteOpen(false)} beforeSubmit={() => (status === "saved" ? null : retry())}/>}
        </PlannerShell>
    );
}

const IconBtn = ({ children, tid, ...p }) => (
    <button {...p} data-testid={tid} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-30">{children}</button>
);

const SaveBadge = ({ status, retry }) => (
    <span className="text-xs font-bold inline-flex items-center gap-1" data-testid="planner-save-status">
        {status === "saved" && <><Check size={12} className="text-emerald-600"/> <span className="text-emerald-700">Saved</span></>}
        {(status === "saving" || status === "dirty") && <><Loader2 size={12} className="animate-spin text-slate-400"/> <span className="text-slate-500">Saving…</span></>}
        {status === "error" && <button onClick={retry} className="text-red-700 inline-flex items-center gap-1"><CloudOff size={12}/> Not saved — retry</button>}
    </span>
);
